import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { Invoice, PaymentMethod } from 'shared';

interface MarkAsPaidDialogProps {
  visible: boolean;
  invoice: Invoice | null;
  onHide: () => void;
  onConfirm: (invoiceId: string, data: {
    receiveDate: string;
    paymentMethod: string;
    notes: string;
    totalReceived: number;
  }) => void;
  loading: boolean;
}

export const MarkAsPaidDialog: React.FC<MarkAsPaidDialogProps> = ({
  visible,
  invoice,
  onHide,
  onConfirm,
  loading,
}) => {
  const [receiveDate, setReceiveDate] = useState<Date>(new Date());
  const [paymentMethod, setPaymentMethod] = useState<string>(PaymentMethod.BANK_TRANSFER);
  const [totalReceived, setTotalReceived] = useState<number>(0);
  const [notes, setNotes] = useState<string>('');

  useEffect(() => {
    if (invoice && visible) {
      setReceiveDate(new Date());
      setPaymentMethod(PaymentMethod.BANK_TRANSFER);
      setTotalReceived(parseFloat(invoice.amount.toString()));
      setNotes('');
    }
  }, [invoice, visible]);

  const invoiceAmount = invoice ? parseFloat(invoice.amount.toString()) : 0;
  const excess = totalReceived - invoiceAmount;

  const paymentMethodOptions = [
    { label: 'Bank Transfer', value: PaymentMethod.BANK_TRANSFER },
    { label: 'Cash', value: PaymentMethod.CASH },
    { label: 'SEPA Debit', value: PaymentMethod.SEPA_DEBIT },
    { label: 'PayPal', value: PaymentMethod.PAYPAL },
    { label: 'Credit Card', value: PaymentMethod.CREDIT_CARD },
    { label: 'Other', value: PaymentMethod.OTHER },
  ];

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);

  const handleConfirm = () => {
    if (!invoice) return;
    onConfirm(invoice.id, {
      receiveDate: receiveDate.toISOString(),
      paymentMethod,
      notes,
      totalReceived,
    });
  };

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
      <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={onHide} disabled={loading} />
      <Button label="Confirm Payment" icon="pi pi-check" onClick={handleConfirm} loading={loading} disabled={!receiveDate || totalReceived <= 0} />
    </div>
  );

  return (
    <Dialog
      header="Mark Invoice as Paid"
      visible={visible}
      onHide={onHide}
      style={{ width: 'min(95vw, 500px)' }}
      footer={footer}
      modal
      closable={!loading}
    >
      <div data-component="mark-as-paid-dialog" style={{ display: 'block' }}>
        {invoice && (
          <>
            <div style={{ background: 'var(--surface-100)', borderRadius: '6px', padding: '1rem', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: 600 }}>Invoice</span>
                <span>{invoice.invoiceNumber}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: 600 }}>Member</span>
                <span>{invoice.member?.firstName} {invoice.member?.lastName}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 600 }}>Amount</span>
                <span style={{ fontWeight: 700 }}>{formatCurrency(invoiceAmount)}</span>
              </div>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Total Received</label>
              <InputNumber
                value={totalReceived}
                onValueChange={(e) => setTotalReceived(e.value ?? 0)}
                mode="currency"
                currency="EUR"
                locale="de-DE"
                min={0.01}
                style={{ width: '100%' }}
              />
            </div>

            {excess > 0.005 && (
              <Message
                severity="info"
                text={`Excess of ${formatCurrency(excess)} will be recorded as a donation`}
                style={{ display: 'block', width: '100%', marginBottom: '0.75rem' }}
              />
            )}

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Receive Date</label>
              <Calendar
                value={receiveDate}
                onChange={(e) => setReceiveDate(e.value as Date)}
                dateFormat="dd.mm.yy"
                showIcon
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Payment Method</label>
              <Dropdown
                value={paymentMethod}
                options={paymentMethodOptions}
                onChange={(e) => setPaymentMethod(e.value)}
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Notes</label>
              <InputTextarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Optional notes..."
                style={{ width: '100%' }}
              />
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};
